//@ts-nocheck
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Clock,
  Waves,
  Star,
  Sun,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ── Japan palette ─────────────────────────────────────────────────────────────
const C = {
  amethyst: "#6D4C92",
  amethystDeep: "#44304e",
  amethystSoft: "rgba(109,76,146,0.08)",
  amethystMid: "rgba(109,76,146,0.18)",
  roseGold: "#E5B8AD",
  roseGoldSoft: "rgba(229,184,173,0.18)",
  pearl: "#F8F4FF",
  pearlDim: "rgba(248,244,255,0.8)",
  sakura: "#F9C6C9",
  gold: "#C4A574",
  goldSoft: "rgba(196,165,116,0.12)",
  textDark: "#2D1B44",
  textMid: "#5D4D6E",
  textMuted: "#9CA3AF",
  white: "#fff",
  pageBg: "linear-gradient(160deg, #F8F4FF 0%, #EDE8F5 50%, #FFF5F5 100%)",
  cardBorder: "rgba(109,76,146,0.13)",
  cardBorderActive: "rgba(109,76,146,0.40)",
  shadow: "0 2px 16px rgba(109,76,146,0.09)",
  shadowLg: "0 6px 32px rgba(109,76,146,0.14)",
};

const JP_FONT = "'Noto Sans JP', sans-serif";

// ── Responsive hook ───────────────────────────────────────────────────────────
const useWindowWidth = () => {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 375,
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
};

// ── Card icon map ─────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ReactNode> = {
  heart: <Heart size={18} strokeWidth={1.8} />,
  clock: <Clock size={18} strokeWidth={1.8} />,
  wave: <Waves size={18} strokeWidth={1.8} />,
  star: <Star size={18} strokeWidth={1.8} />,
  sun: <Sun size={18} strokeWidth={1.8} />,
};

const CARD_ACCENT: Record<string, string> = {
  harmony: C.amethyst,
  timing: "#7B68A8",
  emotional_distance: "#C4859E",
  guidance: C.gold,
  summary: "#8B7BA8",
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface JapanCompatibilityData {
  pages?: Array<{
    pageId: string;
    title: string;
    components?: {
      scoreGauge?: { value: number; label: string };
      lifeGraph?: { categories: string[]; value: number[] };
      summary?: Array<{ type: string; title: string; text: string }>;
    };
    cards?: Array<{
      id: string;
      title: string;
      icon: string;
      description: string;
    }>;
  }>;
}

export interface JapanCompatibilityResultProps {
  compatibilityData: JapanCompatibilityData | null;
  data?: JapanCompatibilityData | null; // Added fallback alias mapping to support `data` prop safely
  selfData?: { blood_type?: string | null; dob?: string | null };
  partnerData?: { blood_type?: string | null; dob?: string | null };
  onBack?: () => void;
  onRetry?: () => void;
}

// ── Radar Chart ───────────────────────────────────────────────────────────────
const RadarChart: React.FC<{
  categories: string[];
  values: number[];
  size: number;
}> = ({ categories, values, size }) => {
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size / 2 - 28;
  const n = categories.length;
  const angle = (i: number) => (i * 2 * Math.PI) / n - Math.PI / 2;

  const pt = (val: number, i: number) => {
    const r = (val / 100) * maxR;
    return { x: cx + r * Math.cos(angle(i)), y: cy + r * Math.sin(angle(i)) };
  };
  const ptFull = (i: number) => {
    const r = maxR;
    return { x: cx + r * Math.cos(angle(i)), y: cy + r * Math.sin(angle(i)) };
  };
  const ptLabel = (i: number) => {
    const r = maxR + 18;
    return { x: cx + r * Math.cos(angle(i)), y: cy + r * Math.sin(angle(i)) };
  };

  const dataPts = values.map((v, i) => pt(v, i));
  const polygon = dataPts.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ overflow: "visible" }}
    >
      {/* Grid rings */}
      {[25, 50, 75, 100].map((lvl) => {
        const ringPts = Array.from({ length: n }, (_, i) => {
          const r = (lvl / 100) * maxR;
          return `${cx + r * Math.cos(angle(i))},${cy + r * Math.sin(angle(i))}`;
        }).join(" ");
        return (
          <polygon
            key={lvl}
            points={ringPts}
            fill="none"
            stroke={lvl === 100 ? C.cardBorderActive : C.cardBorder}
            strokeWidth={lvl === 100 ? 1.2 : 0.8}
            strokeDasharray={lvl === 100 ? "none" : "3,3"}
          />
        );
      })}
      {/* Axis spokes */}
      {Array.from({ length: n }, (_, i) => {
        const p = ptFull(i);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke={C.cardBorder}
            strokeWidth={0.8}
          />
        );
      })}
      {/* Data polygon */}
      <polygon
        points={polygon}
        fill="rgba(109,76,146,0.18)"
        stroke={C.amethyst}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {/* Data dots */}
      {dataPts.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={4}
          fill={C.amethyst}
          stroke={C.white}
          strokeWidth={1.5}
        />
      ))}
      {/* Labels */}
      {categories.map((cat, i) => {
        const p = ptLabel(i);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={size < 160 ? 8.5 : 9.5}
            fill={C.textMid}
            fontFamily={JP_FONT}
          >
            {cat}
          </text>
        );
      })}
    </svg>
  );
};

// ── Score Ring ────────────────────────────────────────────────────────────────
const ScoreRing: React.FC<{ score: number; label: string }> = ({
  score,
  label,
}) => {
  const size = 110;
  const sw = 9;
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;

  const scoreColor =
    score >= 75 ? C.amethyst : score >= 55 ? "#9B7EBD" : C.roseGold;

  return (
    <div style={{ textAlign: "center", flexShrink: 0 }}>
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          margin: "0 auto",
        }}
      >
        <svg width={size} height={size}>
          {/* Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={C.amethystMid}
            strokeWidth={sw}
          />
          {/* Progress */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={scoreColor}
            strokeWidth={sw}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{
              transition: "stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: scoreColor,
              lineHeight: 1,
              fontFamily: JP_FONT,
            }}
          >
            {score}
          </div>
          <div
            style={{
              fontSize: 9,
              color: C.textMuted,
              marginTop: 2,
              fontFamily: JP_FONT,
            }}
          >
            / 100
          </div>
        </div>
      </div>
      {label && (
        <div
          style={{
            fontSize: 11,
            color: C.textMid,
            marginTop: 8,
            fontFamily: JP_FONT,
            lineHeight: 1.4,
            maxWidth: 120,
            margin: "8px auto 0",
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

// ── Expandable Insight Card ───────────────────────────────────────────────────
const InsightCard: React.FC<{
  id: string;
  title: string;
  icon: string;
  description: string;
  index: number;
}> = ({ id, title, icon, description, index }) => {
  const [expanded, setExpanded] = useState(index < 2);
  const accent = CARD_ACCENT[id] || C.amethyst;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.07, duration: 0.35 }}
      style={{
        background: C.white,
        borderRadius: 18,
        border: `1px solid ${C.cardBorder}`,
        boxShadow: C.shadow,
        overflow: "hidden",
        marginBottom: 12,
      }}
    >
      {/* Header row */}
      <button
        onClick={() => setExpanded((e) => !e)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "16px 18px",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {/* Icon circle */}
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${accent}22, ${accent}10)`,
            border: `1.5px solid ${accent}44`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: accent,
          }}
        >
          {ICON_MAP[icon] || <Star size={18} />}
        </div>

        {/* Title */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: C.textDark,
              fontFamily: JP_FONT,
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
        </div>

        {/* Chevron */}
        <div style={{ color: C.textMuted, flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: "hidden" }}
          >
            <div
              style={{
                padding: "0 18px 18px",
                borderTop: `1px solid ${C.cardBorder}`,
                paddingTop: 14,
              }}
            >
              {/* Accent bar */}
              <div
                style={{
                  width: 32,
                  height: 2,
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${accent}, transparent)`,
                  marginBottom: 12,
                }}
              />
              <p
                style={{
                  fontSize: 14,
                  color: C.textMid,
                  lineHeight: 1.85,
                  margin: 0,
                  fontFamily: JP_FONT,
                  whiteSpace: "pre-wrap",
                }}
              >
                {description}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Loading messages cycle ─────────────────────────────────────────────────────
const LOADING_MSGS = [
  { jp: "誕生チャートを生成しています", en: "Generating birth charts…" },
  { jp: "星の流れを読み解いています", en: "Reading the flow of the stars…" },
  { jp: "相性の糸を紡いでいます", en: "Weaving the threads of compatibility…" },
  { jp: "お二人の気を感じています", en: "Sensing the energy between you…" },
  {
    jp: "相性レポートを作成しています",
    en: "Generating your compatibility report…",
  },
];

const FALLING_PETALS = [
  { left: "8%", delay: 0, dur: 4.2, size: 18 },
  { left: "22%", delay: 0.9, dur: 5.0, size: 14 },
  { left: "38%", delay: 1.7, dur: 3.8, size: 22 },
  { left: "55%", delay: 0.3, dur: 4.8, size: 16 },
  { left: "70%", delay: 1.4, dur: 5.3, size: 12 },
  { left: "83%", delay: 0.7, dur: 4.1, size: 20 },
  { left: "94%", delay: 2.1, dur: 3.9, size: 15 },
];

const LoadingView: React.FC<{
  selfBloodType?: string | null;
  partnerBloodType?: string | null;
}> = ({ selfBloodType, partnerBloodType }) => {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length),
      2600,
    );
    return () => clearInterval(t);
  }, []);
  const msg = LOADING_MSGS[msgIdx];

  return (
    <div
      style={{
        position: "relative",
        minHeight: "60vh",
        width: "100%", // Changed from fixed to relative layout to prevent layout drop blank outs
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        fontFamily: JP_FONT,
        padding: "40px 20px",
      }}
    >
      {/* Falling petals */}
      {FALLING_PETALS.map((p, i) => (
        <motion.div
          key={i}
          initial={{ y: -40, opacity: 0, rotate: 0 }}
          animate={{ y: "110vh", opacity: [0, 0.75, 0.75, 0], rotate: 360 }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            left: p.left,
            top: 0,
            fontSize: p.size,
            pointerEvents: "none",
          }}
        >
          🌸
        </motion.div>
      ))}

      {/* Soft glow */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          width: 280,
          height: 280,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(109,76,146,0.1) 0%, rgba(229,184,173,0.06) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Orbiting blood-type dots */}
      <div
        style={{
          position: "relative",
          width: 130,
          height: 130,
          marginBottom: 36,
        }}
      >
        {/* Outer orbit ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "1.5px dashed rgba(109,76,146,0.22)",
          }}
        />
        {/* Self dot */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
          style={{ position: "absolute", inset: 0, borderRadius: "50%" }}
        >
          <div
            style={{
              position: "absolute",
              top: -11,
              left: "50%",
              transform: "translateX(-50%)",
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.amethyst}, #9B7EBD)`,
              boxShadow: `0 0 14px rgba(109,76,146,0.4)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            {selfBloodType || "A"}
          </div>
        </motion.div>

        {/* Inner orbit ring */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            inset: 10,
            borderRadius: "50%",
            border: "1.5px dashed rgba(229,184,173,0.28)",
          }}
        />
        {/* Partner dot */}
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          style={{ position: "absolute", inset: 10, borderRadius: "50%" }}
        >
          <div
            style={{
              position: "absolute",
              bottom: -11,
              left: "50%",
              transform: "translateX(-50%)",
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${C.roseGold}, #C4859E)`,
              boxShadow: "0 0 14px rgba(229,184,173,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              color: "#fff",
              fontWeight: 700,
            }}
          >
            {partnerBloodType || "B"}
          </div>
        </motion.div>

        {/* Centre heart */}
        <motion.div
          animate={{ scale: [0.85, 1.15, 0.85], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 30,
          }}
        >
          💞
        </motion.div>
      </div>

      {/* Rotating message */}
      <AnimatePresence mode="wait">
        <motion.div
          key={msgIdx}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45 }}
          style={{ textAlign: "center", padding: "0 28px", maxWidth: 320 }}
        >
          <p
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: C.textDark,
              margin: "0 0 8px",
              fontFamily: JP_FONT,
              lineHeight: 1.5,
            }}
          >
            {msg.jp}
          </p>
          <p
            style={{
              fontSize: 12,
              color: C.amethyst,
              margin: "0 0 32px",
              fontFamily: JP_FONT,
            }}
          >
            {msg.en}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Shimmer bar */}
      <div
        style={{
          width: 200,
          height: 3,
          borderRadius: 2,
          background: "rgba(109,76,146,0.1)",
          overflow: "hidden",
          marginBottom: 22,
        }}
      >
        <motion.div
          animate={{ x: ["-100%", "200%"] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{
            height: "100%",
            width: "50%",
            borderRadius: 2,
            background: `linear-gradient(90deg, transparent, ${C.amethyst}, transparent)`,
          }}
        />
      </div>

      {/* Pulsing dots */}
      <div style={{ display: "flex", gap: 8 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              delay: i * 0.18,
              ease: "easeInOut",
            }}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: i % 2 === 0 ? C.amethyst : C.roseGold,
            }}
          />
        ))}
      </div>

      {/* Bottom label */}
      <div
        style={{ marginTop: 24, display: "flex", alignItems: "center", gap: 8 }}
      >
        <div
          style={{
            width: 36,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, rgba(109,76,146,0.3))",
          }}
        />
        <span
          style={{
            fontSize: 11,
            color: "rgba(109,76,146,0.45)",
            fontFamily: JP_FONT,
          }}
        >
          ✦ 占い中 ✦
        </span>
        <div
          style={{
            width: 36,
            height: 1,
            background:
              "linear-gradient(90deg, rgba(109,76,146,0.3), transparent)",
          }}
        />
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const JapanCompatibilityResult: React.FC<JapanCompatibilityResultProps> = ({
  compatibilityData,
  data, // Catch fallback data assignment prop safely
  selfData,
  partnerData,
  onBack,
  onRetry,
}) => {
  const width = useWindowWidth();
  const isMobile = width < 480;
  const isTablet = width >= 480 && width < 768;

  // Safe Resolution: Combine both prop alternatives into a single usable reference variable to completely eliminate the white screen.
  const finalData = compatibilityData || data;

  if (!finalData) {
    return (
      <LoadingView
        selfBloodType={selfData?.blood_type}
        partnerBloodType={partnerData?.blood_type}
      />
    );
  }

  // Extract page data
  const p1 = finalData.pages?.find(
    (p) => p.pageId === "P1_JapanCompatibility" || p.pageId?.includes("P1"),
  );
  const p2 = finalData.pages?.find(
    (p) => p.pageId === "P2_DetailedInsights" || p.pageId?.includes("P2"),
  );

  const score = p1?.components?.scoreGauge?.value ?? 0;
  const scoreLabel = p1?.components?.scoreGauge?.label ?? "";
  const radarCats = p1?.components?.lifeGraph?.categories ?? [];
  const radarVals = p1?.components?.lifeGraph?.value ?? [];
  const summaryItems = p1?.components?.summary ?? [];
  const cards = p2?.cards ?? [];

  const positiveItem = summaryItems.find((s) => s.type === "positive");
  const adjustmentItem = summaryItems.find((s) => s.type === "adjustment");

  // Radar size responsive
  const radarSize = isMobile ? 140 : isTablet ? 160 : 175;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{
        minHeight: "100vh",
        background: C.pageBg,
        fontFamily: JP_FONT,
        padding: isMobile ? "16px 14px 48px" : "24px 20px 60px",
      }}
    >
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        {/* ── Page Header ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ textAlign: "center", marginBottom: isMobile ? 24 : 32 }}
        >
          {/* Decorative top element */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, transparent, ${C.amethystMid})`,
              }}
            />
            <span style={{ fontSize: 22 }}>🌸</span>
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, ${C.amethystMid}, transparent)`,
              }}
            />
          </div>

          <h1
            style={{
              fontSize: isMobile ? 20 : 22,
              fontWeight: 700,
              color: C.textDark,
              margin: "0 0 6px",
              fontFamily: JP_FONT,
              letterSpacing: "0.04em",
            }}
          >
            お二人の相性
          </h1>
          <p
            style={{
              fontSize: 12,
              color: C.textMuted,
              margin: 0,
              fontFamily: JP_FONT,
            }}
          >
            Compatibility Reading
          </p>
        </motion.div>

        {/* ── People Summary Strip ──────────────────────────────────────── */}
        {(selfData || partnerData) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              gap: isMobile ? 8 : 12,
              marginBottom: isMobile ? 16 : 20,
              alignItems: "center",
            }}
          >
            {[
              { label: "あなた", emoji: "🌸", data: selfData },
              null,
              { label: "相手", emoji: "🌷", data: partnerData },
            ].map((item, idx) =>
              item === null ? (
                <div key="mid" style={{ textAlign: "center" }}>
                  <span style={{ fontSize: 20 }}>💞</span>
                </div>
              ) : (
                <div
                  key={idx}
                  style={{
                    background: C.white,
                    borderRadius: 14,
                    border: `1px solid ${C.cardBorder}`,
                    padding: isMobile ? "10px 10px" : "12px 14px",
                    boxShadow: C.shadow,
                    textAlign: idx === 2 ? "right" : "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: C.amethyst,
                      marginBottom: 4,
                      fontFamily: JP_FONT,
                    }}
                  >
                    {item.emoji} {item.label}
                  </div>
                  {item.data?.blood_type && (
                    <div
                      style={{
                        fontSize: 12,
                        color: C.textDark,
                        fontFamily: JP_FONT,
                        fontWeight: 600,
                      }}
                    >
                      血液型 {item.data.blood_type}型
                    </div>
                  )}
                  {item.data?.dob && (
                    <div
                      style={{
                        fontSize: 11,
                        color: C.textMuted,
                        marginTop: 2,
                        fontFamily: JP_FONT,
                      }}
                    >
                      {item.data.dob}
                    </div>
                  )}
                </div>
              ),
            )}
          </motion.div>
        )}

        {/* ── Score + Radar Card ────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: C.white,
            borderRadius: 22,
            border: `1px solid ${C.cardBorder}`,
            boxShadow: C.shadowLg,
            padding: isMobile ? "20px 16px" : "24px 24px",
            marginBottom: isMobile ? 16 : 20,
          }}
        >
          {/* Score + Radar side by side */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              gap: isMobile ? 12 : 20,
              flexWrap: "wrap",
            }}
          >
            <ScoreRing score={score} label={scoreLabel} />
            {radarCats.length > 0 && radarVals.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <RadarChart
                  categories={radarCats}
                  values={radarVals}
                  size={radarSize}
                />
              </div>
            )}
          </div>

          {/* Summary items */}
          {(positiveItem || adjustmentItem) && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 18,
                borderTop: `1px solid ${C.cardBorder}`,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {positiveItem && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        background: C.amethyst,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.amethyst,
                        fontFamily: JP_FONT,
                      }}
                    >
                      {positiveItem.title}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: C.textMid,
                      lineHeight: 1.7,
                      margin: 0,
                      fontFamily: JP_FONT,
                    }}
                  >
                    {positiveItem.text}
                  </p>
                </div>
              )}
              {adjustmentItem && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        background: C.roseGold,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#C4859E",
                        fontFamily: JP_FONT,
                      }}
                    >
                      {adjustmentItem.title}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: C.textMid,
                      lineHeight: 1.7,
                      margin: 0,
                      fontFamily: JP_FONT,
                    }}
                  >
                    {adjustmentItem.text}
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* ── Section divider ───────────────────────────────────────────── */}
        {cards.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: isMobile ? 14 : 18,
            }}
          >
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, transparent, ${C.amethystMid})`,
              }}
            />
            <span
              style={{
                fontSize: 11,
                color: C.amethyst,
                fontFamily: JP_FONT,
                whiteSpace: "nowrap",
              }}
            >
              詳しい読み解き
            </span>
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, ${C.amethystMid}, transparent)`,
              }}
            />
          </div>
        )}

        {/* ── Insight Cards ─────────────────────────────────────────────── */}
        {cards.map((card, idx) => (
          <InsightCard
            key={card.id}
            id={card.id}
            title={card.title}
            icon={card.icon}
            description={card.description}
            index={idx}
          />
        ))}

        {/* ── Action Buttons ────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={{
            display: "flex",
            gap: 10,
            marginTop: isMobile ? 24 : 32,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {onRetry && (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onRetry}
              style={{
                flex: 1,
                height: 50,
                borderRadius: 14,
                border: `1.5px solid ${C.cardBorderActive}`,
                background: "transparent",
                color: C.amethyst,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: JP_FONT,
                cursor: "pointer",
              }}
            >
              相手を変える
            </motion.button>
          )}
          {onBack && (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onBack}
              style={{
                flex: 1,
                height: 50,
                borderRadius: 14,
                background: `linear-gradient(135deg, ${C.amethyst} 0%, ${C.amethystDeep} 100%)`,
                border: "none",
                color: C.white,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: JP_FONT,
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(109,76,146,0.25)",
              }}
            >
              最初からやり組む
            </motion.button>
          )}
        </motion.div>

        {/* ── Footer note ───────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", marginTop: 28 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, transparent, ${C.amethystMid})`,
              }}
            />
            <span style={{ fontSize: 14 }}>✦</span>
            <div
              style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg, ${C.amethystMid}, transparent)`,
              }}
            />
          </div>
          <p
            style={{
              fontSize: 11,
              color: C.textMuted,
              fontFamily: JP_FONT,
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            この読み解きは星と気の静かな観察です。
            <br />
            <span style={{ fontSize: 10 }}>
              This is a gentle observation of stars and energy — not a
              prediction.
            </span>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default JapanCompatibilityResult;
export { LoadingView as JapanCompatibilityLoadingView };
